/**
 * The one watchlist-name rule (SE-8449): 50 characters after trimming,
 * non-empty, and not a case-variant of a name the client already knows.
 * Every door that names a list — the rename modal, Name it, the create-list
 * sheet and the desktop inline rename — validates through this module so the
 * cap and the messages cannot drift apart again. The backend enforces the
 * same cap and the case-insensitive uniqueness; `ERR_WATCHLIST_NAME_EXISTS`
 * still arrives for names the client could not see (another user's list in
 * the organization), so the pre-check here is an early inline error, not the
 * authority.
 * @module features/scouting/utils/watchlistNameRules
 */

import * as yup from "yup";

/** Maximum list-name length, measured after trimming. */
export const WATCHLIST_NAME_MAX = 50;

/** Shown for an empty or whitespace-only name. */
export const WATCHLIST_NAME_REQUIRED_MESSAGE = "List name is required";

/** Shown for a name longer than {@link WATCHLIST_NAME_MAX} after trimming. */
export const WATCHLIST_NAME_TOO_LONG_MESSAGE = `Name must be ${WATCHLIST_NAME_MAX} characters or fewer`;

/** Shown for a name already used by a list the client knows (SE-8415 copy). */
export const WATCHLIST_NAME_TAKEN_MESSAGE = "Name already taken";

/**
 * The comparison key for the duplicate check.
 * @param name - A list name as typed or as stored
 * @returns The name trimmed and case-folded.
 */
const nameKey = (name: string): string => name.trim().toLowerCase();

/**
 * Whether `name` is a case-insensitive match, after trimming, of any name in
 * `existingNames`.
 * @param name - The candidate name
 * @param existingNames - Names the client already knows (the current list's own
 * name must be excluded by the caller when renaming)
 * @returns True when the name is taken.
 */
export const isWatchlistNameTaken = (
  name: string,
  existingNames: readonly string[]
): boolean => {
  const key = nameKey(name);
  return existingNames.some(existing => nameKey(existing) === key);
};

/**
 * Builds the yup schema for a list name: trimmed, required, capped at
 * {@link WATCHLIST_NAME_MAX}, and not taken among `existingNames`.
 * @param existingNames - Names the client already knows
 * @returns The string schema (yields the trimmed name).
 */
export const buildWatchlistNameSchema = (
  existingNames: readonly string[] = []
): yup.StringSchema<string> =>
  yup
    .string()
    .trim()
    .required(WATCHLIST_NAME_REQUIRED_MESSAGE)
    .max(WATCHLIST_NAME_MAX, WATCHLIST_NAME_TOO_LONG_MESSAGE)
    .test(
      "watchlist-name-taken",
      WATCHLIST_NAME_TAKEN_MESSAGE,
      value => !isWatchlistNameTaken(value ?? "", existingNames)
    );

/**
 * The inline message for a draft name, or null when it is valid.
 * @param name - The draft as typed (untrimmed)
 * @param existingNames - Names the client already knows
 * @returns The first failing rule's message, or null.
 */
export const watchlistNameError = (
  name: string,
  existingNames: readonly string[] = []
): string | null => {
  try {
    buildWatchlistNameSchema(existingNames).validateSync(name);
    return null;
  } catch (error) {
    return error instanceof yup.ValidationError
      ? error.message
      : WATCHLIST_NAME_REQUIRED_MESSAGE;
  }
};
